// Teklif takip hareketleri (görüşmeler) — kim, kiminle, ne zaman, durum, açıklama.
// Embedded olarak bir teklif formu içinde kullanılır.
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button, Modal, FormField, EmptyState } from './ui';
import SearchableSelect from './SearchableSelect';

const IconPlus  = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const IconEdit  = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>);
const IconTrash = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>);

const fmtDateTime = (s) => s ? new Date(s).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

// datetime-local input değeri
const toLocalDT = (s) => {
  const d = s ? new Date(s) : new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function QuoteActivities({ quoteTable, quoteId, quoteStatuses = [], canEdit, users = [] }) {
  const { user: currentUser } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!quoteTable || !quoteId) return;
    setLoading(true);
    try {
      const r = await api.get('/quote-activities', { params: { quote_table: quoteTable, quote_id: quoteId } });
      if (r.data.success) setList(r.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [quoteTable, quoteId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setError('');
    setEditing({
      activity_at: toLocalDT(),
      contact_name: '',
      user_id: currentUser?.id || currentUser?.userId || null,
      quote_status_id: null,
      notes: '',
      next_meeting_at: '',
    });
  };
  const openEdit = (row) => {
    setError('');
    setEditing({
      id: row.id,
      activity_at: toLocalDT(row.activity_at),
      contact_name: row.contact_name || '',
      user_id: row.user_id,
      quote_status_id: row.quote_status_id,
      notes: row.notes || '',
      next_meeting_at: row.next_meeting_at ? toLocalDT(row.next_meeting_at) : '',
    });
  };
  const cancel = () => { setEditing(null); setError(''); };

  const save = async () => {
    if (!editing.notes.trim() && !editing.contact_name.trim()) {
      setError('En az açıklama veya görüşülen kişi gerekli');
      return;
    }
    setSaving(true); setError('');
    try {
      const payload = {
        quote_table: quoteTable,
        quote_id: quoteId,
        activity_at: new Date(editing.activity_at).toISOString(),
        contact_name: editing.contact_name.trim() || null,
        user_id: editing.user_id || null,
        quote_status_id: editing.quote_status_id || null,
        notes: editing.notes.trim() || null,
        next_meeting_at: editing.next_meeting_at ? new Date(editing.next_meeting_at).toISOString() : null,
      };
      if (editing.id) await api.put('/quote-activities/' + editing.id, payload);
      else            await api.post('/quote-activities', payload);
      setEditing(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm('Bu görüşme kaydı silinsin mi?')) return;
    try {
      await api.delete('/quote-activities/' + row.id);
      await load();
    } catch (e) { alert(e.response?.data?.message || 'Silme başarısız'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={{ margin: 0, flex: 1, fontSize: 14, fontWeight: 600 }}>Görüşme / Takip Hareketleri</h4>
        {canEdit && <Button size="sm" icon={<IconPlus />} onClick={openNew}>Yeni Görüşme</Button>}
      </div>

      {loading && <div style={{ padding: 12, color: 'var(--text-tertiary)', fontSize: 13 }}>Yükleniyor...</div>}
      {!loading && list.length === 0 && (
        <EmptyState title="Henüz görüşme yok" description="İlk görüşme/takip kaydını oluşturmak için yukarıdaki butona tıklayın." compact />
      )}

      {list.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map(a => (
            <div key={a.id} style={{
              background: 'var(--bg-primary, #fff)',
              border: '1px solid var(--border-color, #e5e5e5)',
              borderLeft: a.quote_status_color ? `4px solid ${a.quote_status_color}` : '4px solid var(--border-color, #e5e5e5)',
              borderRadius: 6,
              padding: '10px 12px',
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 10, marginBottom: 6 }}>
                <strong style={{ fontSize: 13 }}>📅 {fmtDateTime(a.activity_at)}</strong>
                {a.quote_status_name && (
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                    background: (a.quote_status_color || '#64748b') + '22',
                    color: a.quote_status_color || '#64748b',
                  }}>{a.quote_status_name}</span>
                )}
                {canEdit && (
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                    <Button variant="ghost" size="sm" iconOnly icon={<IconEdit />} title="Düzenle" onClick={() => openEdit(a)} />
                    <Button variant="ghost" size="sm" iconOnly icon={<IconTrash />} title="Sil" onClick={() => remove(a)} />
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, marginBottom: a.notes || a.next_meeting_at ? 6 : 0 }}>
                <span>
                  <span style={{ color: 'var(--text-tertiary)' }}>Görüşen: </span>
                  <strong style={{ color: 'var(--text-primary)' }}>👤 {a.user_full_name || a.user_name_snapshot || '—'}</strong>
                </span>
                <span>
                  <span style={{ color: 'var(--text-tertiary)' }}>Görüşülen: </span>
                  <strong style={{ color: 'var(--text-primary)' }}>↔ {a.contact_name || '—'}</strong>
                </span>
              </div>
              {a.notes && <div style={{ fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'pre-wrap', marginBottom: a.next_meeting_at ? 6 : 0 }}>{a.notes}</div>}
              {a.next_meeting_at && (
                <div style={{ fontSize: 12, color: 'var(--success)', fontWeight: 600 }}>
                  📅 Sonraki Randevu: {fmtDateTime(a.next_meeting_at)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal
          title={editing.id ? 'Görüşmeyi Düzenle' : 'Yeni Görüşme'}
          size="md"
          onClose={cancel}
          primaryAction={{ label: 'Kaydet', onClick: save, loading: saving }}
          cancelAction={{ label: 'Vazgeç', onClick: cancel }}
        >
          <FormField label="Tarih / Saat" required>
            <input type="datetime-local" value={editing.activity_at} onChange={e => setEditing({ ...editing, activity_at: e.target.value })} />
          </FormField>
          <FormField label="Kim Görüştü (kullanıcı)" hint="Boş bırakılırsa cari sıfat kabul edilmez">
            <SearchableSelect
              value={editing.user_id}
              onChange={(id) => setEditing({ ...editing, user_id: id })}
              options={users}
              getLabel={u => u.full_name || u.name || u.code}
              getSubLabel={u => u.code}
              searchKeys={['full_name', 'name', 'code']}
              placeholder="Personel seçin..."
              emptyText="Personel yok"
            />
          </FormField>
          <FormField label="Kiminle Görüşüldü" hint="Müşteri/tedarikçi yetkilisinin adı">
            <input type="text" value={editing.contact_name} onChange={e => setEditing({ ...editing, contact_name: e.target.value })} placeholder="Ahmet Bey" />
          </FormField>
          <FormField label="Sonuç Durumu" hint="Görüşme sonrası teklifin yeni durumu (opsiyonel — seçilirse teklif bu duruma geçer)">
            <select value={editing.quote_status_id || ''} onChange={e => setEditing({ ...editing, quote_status_id: e.target.value || null })} style={{ width: '100%', padding: '8px 10px' }}>
              <option value="">— Değişiklik yok —</option>
              {quoteStatuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </FormField>
          <FormField label="Açıklama / Notlar">
            <textarea rows={5} value={editing.notes} onChange={e => setEditing({ ...editing, notes: e.target.value })} placeholder="Görüşmenin özeti..." style={{ width: '100%', resize: 'vertical' }} />
          </FormField>
          <FormField label="📅 Sonraki Görüşme Randevusu" hint="Bir sonraki temas için tarih/saat (opsiyonel)">
            <input type="datetime-local" value={editing.next_meeting_at || ''} onChange={e => setEditing({ ...editing, next_meeting_at: e.target.value })} />
          </FormField>

          {/* Önceki görüşmeler — formun üst kısmında özet liste (en yeni üstte) */}
          {list.length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed var(--border-secondary)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Önceki Görüşmeler ({list.length})
              </div>
              <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {list.filter(a => a.id !== editing.id).map(a => (
                  <div key={a.id} style={{
                    padding: '6px 10px', borderRadius: 6, background: 'var(--bg-secondary)',
                    fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2,
                  }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <strong>{fmtDateTime(a.activity_at)}</strong>
                      {a.user_full_name && <span style={{ color: 'var(--text-secondary)' }}>👤 {a.user_full_name}</span>}
                      {a.contact_name && <span style={{ color: 'var(--text-secondary)' }}>↔ {a.contact_name}</span>}
                      {a.quote_status_name && (
                        <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 700,
                          background: (a.quote_status_color || '#64748b') + '22', color: a.quote_status_color || '#64748b',
                        }}>{a.quote_status_name}</span>
                      )}
                    </div>
                    {a.notes && <div style={{ color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{a.notes}</div>}
                    {a.next_meeting_at && (
                      <div style={{ color: 'var(--success)' }}>📅 Sonraki: {fmtDateTime(a.next_meeting_at)}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {error && <div className="ui-field-error" style={{ marginTop: 8 }}>{error}</div>}
        </Modal>
      )}
    </div>
  );
}
